import * as React from 'react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/overlays/tooltip';

const LINE_CLAMP: Record<number, string> = {
  1: 'truncate',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
};

export interface TruncatedTextProps {
  /** The text to display */
  text: string;
  /** Additional CSS classes for the text span */
  className?: string;
  /** Maximum width for the tooltip content */
  tooltipMaxWidth?: string;
  /** Number of visible lines before clamping. Defaults to 1. */
  lines?: number;
}

/**
 * A smart text component that truncates with ellipsis and only shows
 * a tooltip when the text is actually truncated/clamped.
 *
 * Supports single-line truncation (default) and multi-line clamping
 * via the `lines` prop.
 *
 * @example
 * <TruncatedText text="Short text on one line" />
 * <TruncatedText text="Long text that wraps to two lines" lines={2} />
 */
export function TruncatedText({
  text,
  className,
  tooltipMaxWidth = '400px',
  lines = 1,
}: TruncatedTextProps) {
  const textRef = React.useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    const checkTruncation = () => {
      const el = textRef.current;
      if (!el) return;
      if (lines <= 1) {
        setIsTruncated(el.scrollWidth > el.clientWidth);
      } else {
        setIsTruncated(el.scrollHeight > el.clientHeight);
      }
    };

    // Run once now and once in the next frame so late layout/font changes
    // (common in dense data-tables) still compute tooltip visibility correctly.
    checkTruncation();
    const raf = window.requestAnimationFrame(checkTruncation);

    const el = textRef.current;

    let ro: ResizeObserver | null = null;
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        checkTruncation();
      });
      ro.observe(el);
    }

    // Re-measure when the element becomes visible. In expandable/animated
    // containers (e.g. tree-table child rows) the text mounts while collapsed
    // (0-height, overflow hidden), so the initial measurement is unreliable and
    // the ResizeObserver never re-fires because the span's own box never
    // changes size. Watching intersection catches the moment it's revealed.
    let io: IntersectionObserver | null = null;
    if (el && typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          checkTruncation();
        }
      });
      io.observe(el);
    }

    window.addEventListener('resize', checkTruncation);
    return () => {
      window.cancelAnimationFrame(raf);
      ro?.disconnect();
      io?.disconnect();
      window.removeEventListener('resize', checkTruncation);
    };
  }, [text, lines]);

  const clampClass = LINE_CLAMP[lines] ?? `line-clamp-${lines}`;

  const content = (
    <span
      ref={textRef}
      className={cn('block break-words', clampClass, className)}
    >
      {text}
    </span>
  );

  if (isTruncated) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {content}
        </TooltipTrigger>
        <TooltipContent side="top" style={{ maxWidth: tooltipMaxWidth }}>
          {text}
        </TooltipContent>
      </Tooltip>
    );
  }

  return content;
}
